/* eslint-disable max-lines */
import { addMonths } from 'date-fns';

import { PAYMENT_METHOD_FIELDS, PHASE_FIELDS } from 'lib/constants/healthcareProgram/paymentSetup';
import { formatDate } from 'lib/helpers/dateUtils';
import { mString } from 'lib/helpers/utility';

export const getRecurringChargeConfig = (config) =>
  (config?.payment_methods ?? []).find((m) => m.field === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE)
    ?.config ?? null;

const computeMaxPayments = (frequency, durationMonths, repeatEvery = 1) => {
  const every = Math.max(1, Number(repeatEvery) || 1);
  if (frequency === 'weekly') return Math.max(1, Math.floor((durationMonths * 4) / every));
  if (frequency === 'yearly') return Math.max(1, Math.floor(durationMonths / 12 / every));
  return Math.max(1, Math.floor(durationMonths / every));
};

export const getRecurringCaps = (config) => {
  const recurringConfig = getRecurringChargeConfig(config);
  if (!recurringConfig) return null;
  const maxAmount = Number(recurringConfig.amount);
  return {
    frequency: recurringConfig.frequency || 'monthly',
    maxAmountPerPayment: Number.isFinite(maxAmount) && maxAmount > 0 ? maxAmount : null,
    maxDurationMonths: Math.max(1, Number(recurringConfig.duration_months) || 6),
    lockType: 'installments'
  };
};

export { computeMaxPayments };

export const buildRecurringDefaults = ({ config, balance, timezone, phaseField }) => {
  const recurringConfig = getRecurringChargeConfig(config);
  const frequency = recurringConfig?.frequency || 'monthly';
  const durationMonths = Number(recurringConfig?.duration_months) || 6;
  const requireApproval = Boolean(recurringConfig?.require_approval);
  const amount = Number(balance) > 0 ? Number(balance) : Number(recurringConfig?.amount) || 0;
  const today = new Date();
  const startDate = formatDate(today, timezone, 'MM/dd/yyyy');

  const numberOfPayments = computeMaxPayments(frequency, durationMonths, 1);

  const endsDate = formatDate(addMonths(today, durationMonths), timezone, 'MM/dd/yyyy');

  return {
    type: 'installments',
    paymentType: 'number-of-payments',
    amount,
    upfrontPayment: 0,
    startDate,
    repeatEvent: frequency,
    repeatEvery: 1,
    numberOfPayments,
    amountPerPayment: null,
    preAuthRequired: true,
    requireApproval,
    endsDate,
    phaseField
  };
};

export const LIVE_PREVIEW_TAB_KEYS = {
  GOOD_FAITH_ESTIMATE: 'good-faith-estimate',
  PATIENT_OPTION_LETTER: 'patient-option-letter',
  PRE_AUTH_FORM: 'pre-auth-form'
};

export const LIVE_PREVIEW_TABS = [
  { key: LIVE_PREVIEW_TAB_KEYS.GOOD_FAITH_ESTIMATE, label: 'Good faith estimate' },
  { key: LIVE_PREVIEW_TAB_KEYS.PATIENT_OPTION_LETTER, label: 'Patient option letter' },
  { key: LIVE_PREVIEW_TAB_KEYS.PRE_AUTH_FORM, label: 'Pre-auth form' }
];

const PHASE_LABEL = {
  [PHASE_FIELDS.IN_NETWORK]: 'In-Network',
  [PHASE_FIELDS.OUT_OF_NETWORK]: 'Out-of-Network',
  [PHASE_FIELDS.SELF_PAY]: 'Self-Pay'
};

const AUTHORIZATION_APPLIED_LABEL = {
  'all-visits-this-year': 'All visits this year',
  'this-visit': 'This visit',
  'let-patient-choose': 'Patient chooses'
};

const CARD_ON_FILE_LABEL = {
  'require-card-on-file': 'COF Required',
  'patient-option': 'COF Optional'
};

const PATIENT_PORTION_CPT_LABEL = {
  'self-pay-amount': 'Self-pay',
  'charge-amount': 'Charge',
  'allowed-amount-ucr': 'UCR'
};

const isRecurringEnabled = (paymentMethods = []) => {
  const recurring = paymentMethods.find((m) => m.field === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE);
  return Boolean(recurring && (recurring.in_person || recurring.remote));
};

const CAP_LIKE_LIMIT_TYPES = new Set([
  'set-dollar-amount',
  'set-higher-allowable-amount',
  'down-payment'
]);

// All cap amounts are CENTS (DB + CurrencyInput store cents). mString
// divides by 100 (cents=true default) for display.
const cap = (amount, label, isCap) => ({ label, isCap, amount });

const getCapDescriptor = (phase, amounts = {}, oonResponsibility = null) => {
  if (!phase) return cap(null, 'the combined allowable amount', false);

  const combinedAllowable = Number(amounts.combined_allowable);
  const selfPay = Number(amounts.self_pay);
  const ucr = Number(amounts.ucr);
  const hasCombinedAllowable = Number.isFinite(combinedAllowable) && combinedAllowable > 0;
  const hasSelfPay = Number.isFinite(selfPay) && selfPay > 0;
  const hasUcr = Number.isFinite(ucr) && ucr > 0;

  if (phase.field === PHASE_FIELDS.IN_NETWORK) {
    const route = phase.charge_limit?.route_to;
    const config = phase.charge_limit?.config ?? {};
    if (route === 'set-dollar-amount') {
      const v = Number(config.dollar_cap) || 0;
      return cap(v, mString(v), true);
    }
    if (route === 'set-higher-allowable-amount') {
      const rawPct = Number(config.percent_above ?? 10);
      const pct = Number.isFinite(rawPct) ? rawPct : 10;
      if (hasCombinedAllowable) {
        const v = combinedAllowable * (1 + pct / 100);
        return cap(v, `${mString(v)} (${pct}% above combined allowable)`, true);
      }
      return cap(null, `${pct}% above the combined allowable amount`, true);
    }
    if (route === 'full-cost') {
      return hasSelfPay
        ? cap(selfPay, mString(selfPay), true)
        : cap(null, 'the full self-pay cost of services rendered', false);
    }
    return hasCombinedAllowable
      ? cap(combinedAllowable, mString(combinedAllowable), true)
      : cap(null, 'the combined allowable amount', false);
  }

  if (phase.field === PHASE_FIELDS.OUT_OF_NETWORK) {
    if (phase.billing_method?.route_to === 'down-payment') {
      const downPayment = Number(phase.billing_method?.config?.down_payment_amount) || 0;
      const hasDp = downPayment > 0;
      return hasDp
        ? cap(downPayment, mString(downPayment), true)
        : cap(null, 'the down payment amount', true);
    }
    if (phase.billing_method?.route_to === 'eligibility-calculation') {
      const netDue = Number(oonResponsibility?.net_due_cents);
      if (Number.isFinite(netDue) && netDue >= 0) {
        return cap(netDue, mString(netDue), true);
      }
    }
    const portion = phase.patient_portion_cpt?.route_to;
    if (portion === 'charge-amount') {
      return hasSelfPay
        ? cap(selfPay, mString(selfPay), false)
        : cap(null, 'the full billed charge amount', false);
    }
    if (portion === 'allowed-amount-ucr') {
      return hasUcr
        ? cap(ucr, mString(ucr), false)
        : cap(null, 'the allowable amount (UCR)', false);
    }
    return hasSelfPay
      ? cap(selfPay, mString(selfPay), false)
      : cap(null, `the practice's self-pay amount per CPT code`, false);
  }

  if (phase.field === PHASE_FIELDS.SELF_PAY) {
    const route = phase.charge_limit?.route_to;
    const config = phase.charge_limit?.config ?? {};
    if (route === 'set-dollar-amount') {
      const v = Number(config.dollar_cap) || 0;
      return cap(v, mString(v), true);
    }
    return cap(null, 'the full cost of treatments actually received', false);
  }

  return cap(null, 'the combined allowable amount', false);
};

const getScopeClause = (route) => {
  if (route === 'all-visits-this-year') return ' for this and all visits this calendar year';
  if (route === 'let-patient-choose') {
    return ' for the visits authorized on the Pre-Authorized Healthcare Form';
  }
  return '';
};

export const getHealthcareProgramDisclosureCopy = (phase, amounts, oonResponsibility = null) => {
  const choiceClause =
    phase?.card_on_file?.route_to === 'require-card-on-file' ? 'are required to' : 'may choose to';
  const scopeClause = getScopeClause(phase?.authorization_applied?.route_to);
  const { label: capLabel, isCap } = getCapDescriptor(phase, amounts, oonResponsibility);

  return { choiceClause, scopeClause, capLabel, isCap };
};

export const isCapLikeLimit = (route) => CAP_LIKE_LIMIT_TYPES.has(route);

export const isSelfPayPhase = (phase) => phase?.field === PHASE_FIELDS.SELF_PAY;

export const getCapLabel = (phase, amounts, oonResponsibility = null) => {
  const { label, isCap, amount } = getCapDescriptor(phase, amounts, oonResponsibility);
  return { capLabel: label, isCap, capAmount: amount };
};

export const PHASE_DRIVEN_COPY = {
  estimatedBalance: {
    selfPayTitle: 'Estimated cost for your visit $282.00',
    title: 'Estimated balance due $282.00',
    selfPaySubtitle:
      'This is an estimated cost for your visit. Final charges may vary based on services rendered.',
    subtitle:
      'This is an estimated balance due for your visit. Your final cost may change after insurance is processed.'
  },
  paymentMethods: {
    selfPaySubtitle: 'To support transparency and flexible care, we’ve updated our payment policy.',
    subtitle: 'To support transparency and flexible care, we’ve updated our payment policy.',
    guarantee: {
      selfPayTitle: 'Guarantee future balance on my credit card',
      title: 'Guarantee any amount not covered by insurance on my credit card'
    },
    capNoteLabel: 'Maximum auto-charge cap:',
    capNoteSuffix:
      'Any charge above this amount will be sent to you as an invoice before processing.'
  }
};

export const getLivePreviewPhaseChips = (phase, paymentMethods) => {
  if (!phase) return [];

  const chips = [];

  const phaseLabel = PHASE_LABEL[phase.field];
  if (phaseLabel) chips.push(phaseLabel);

  const cofLabel = CARD_ON_FILE_LABEL[phase.card_on_file?.route_to];
  if (cofLabel) chips.push(cofLabel);

  const authLabel = AUTHORIZATION_APPLIED_LABEL[phase.authorization_applied?.route_to];
  if (authLabel) chips.push(authLabel);

  if (phase.field === PHASE_FIELDS.OUT_OF_NETWORK) {
    const ppc = PATIENT_PORTION_CPT_LABEL[phase.patient_portion_cpt?.route_to];
    if (ppc) chips.push(ppc);
  }

  if (isRecurringEnabled(paymentMethods)) chips.push('Recurring');

  return chips;
};

const DEFAULT_GRACE_DAYS = '90';

const balanceAboveTerm = [
  { text: 'Any balance above the authorized amount', bold: true },
  { text: ' may be invoiced via a secure link.' }
];

const graceDaysTerm = (days) => [
  { text: `If payment is not made within ${days} days`, bold: true },
  { text: ', your card may be charged for the remaining balance.' }
];

export const buildPreAuthTerms = (
  phase,
  graceDays = DEFAULT_GRACE_DAYS,
  amounts,
  oonResponsibility = null
) => {
  const { capLabel } = getHealthcareProgramDisclosureCopy(phase, amounts, oonResponsibility);

  if (phase?.field === PHASE_FIELDS.IN_NETWORK) {
    const isCap = isCapLikeLimit(phase?.charge_limit?.route_to);
    return [
      [
        { text: 'If the final patient portion is less than the GFE', bold: true },
        { text: ', the difference will be refunded to the card on file.' }
      ],
      [
        { text: 'If greater', bold: true },
        { text: ', the card may be charged up to ' },
        { text: capLabel, bold: true },
        { text: isCap ? ' (should match the authorized amount above).' : '.' }
      ],
      [
        { text: 'Any balance above that amount', bold: true },
        { text: ' may be invoiced via a secure link.' }
      ],
      graceDaysTerm(graceDays)
    ];
  }

  if (phase?.field === PHASE_FIELDS.OUT_OF_NETWORK) {
    const balanceBillingTerm = phase?.balance_billing?.enabled
      ? [
          [
            { text: 'Balance billing', bold: true },
            {
              text: ' — any remaining balance after insurance may be billed, not to exceed your practice fees for the services rendered.'
            }
          ]
        ]
      : [];

    if (phase?.billing_method?.route_to === 'down-payment') {
      return [
        [
          { text: 'A down payment', bold: true },
          { text: ' will be collected before treatment is rendered.' }
        ],
        ...balanceBillingTerm,
        balanceAboveTerm,
        graceDaysTerm(graceDays)
      ];
    }
    return [
      [
        { text: 'Your out-of-network benefits', bold: true },
        {
          text: ' will be used to calculate your estimated responsibility (deductible, coinsurance, copayment).'
        }
      ],
      [
        { text: 'If the final patient portion is less than the estimate', bold: true },
        { text: ', the difference will be refunded to the card on file.' }
      ],
      [
        { text: 'If greater', bold: true },
        { text: ', the card may be charged up to ' },
        { text: `${capLabel}.`, bold: true }
      ],
      ...balanceBillingTerm,
      balanceAboveTerm,
      graceDaysTerm(graceDays)
    ];
  }

  if (phase?.field === PHASE_FIELDS.SELF_PAY) {
    return [
      [
        { text: 'Self-pay services', bold: true },
        { text: " are billed at the practice's self-pay rate for services rendered." }
      ],
      [
        { text: 'Your card may be charged', bold: true },
        { text: ' up to ' },
        { text: capLabel, bold: true },
        { text: ' for services actually received.' }
      ],
      [
        { text: 'Any balance above that amount', bold: true },
        { text: ' may be invoiced via a secure link.' }
      ],
      graceDaysTerm(graceDays)
    ];
  }

  return [];
};

export const PRE_AUTH_PHASE_COPY = {
  statement: {
    selfPayPrefix: 'Patient guarantees the cost of self-pay services rendered, up to ',
    insuredPrefix: 'Patient guarantees any balances not paid by insurance within ',
    insuredDaysSuffix: ' days of treatment, not to exceed '
  }
};

export const SCOPE_OPTIONS = {
  VISIT: 'this-visit',
  YEAR: 'all-visits-this-year'
};

const ROUTE_TO_SCOPE = {
  'this-visit': SCOPE_OPTIONS.VISIT,
  'all-visits-this-year': SCOPE_OPTIONS.YEAR,
  'let-patient-choose': SCOPE_OPTIONS.VISIT
};

export const getDefaultScope = (phase) =>
  ROUTE_TO_SCOPE[phase?.authorization_applied?.route_to] ?? null;

export const getScopeLabel = (route) => {
  if (route === SCOPE_OPTIONS.YEAR) return 'all visits this year';
  if (route === SCOPE_OPTIONS.VISIT) return 'this visit only';
  return '';
};

export const getPreAuthStatementParts = (
  phase,
  graceDays = DEFAULT_GRACE_DAYS,
  amounts,
  oonResponsibility = null
) => {
  const { capLabel } = getHealthcareProgramDisclosureCopy(phase, amounts, oonResponsibility);
  return {
    isSelfPay: isSelfPayPhase(phase),
    capLabel,
    graceDays,
    scopeLabel: getScopeLabel(phase?.authorization_applied?.route_to)
  };
};

export const allowedCards = [
  { icon: 'cards-visuals-visa', id: 1 },
  { icon: 'cards-visuals-mastercard', id: 2 },
  { icon: 'cards-visuals-amex', id: 3 },
  { icon: 'cards-visuals-discover', id: 4 }
];

export const LETTER_VIEWS = {
  GOOD_FAITH_ESTIMATE: LIVE_PREVIEW_TAB_KEYS.GOOD_FAITH_ESTIMATE,
  PATIENT_OPTION_LETTER: LIVE_PREVIEW_TAB_KEYS.PATIENT_OPTION_LETTER,
  PRE_AUTH_FORM: LIVE_PREVIEW_TAB_KEYS.PRE_AUTH_FORM
};

export const getLetterView = ({ showPreAuthForm, letterType }) => {
  if (showPreAuthForm) return LETTER_VIEWS.PRE_AUTH_FORM;
  if (letterType === LETTER_VIEWS.PATIENT_OPTION_LETTER) return LETTER_VIEWS.PATIENT_OPTION_LETTER;
  return LETTER_VIEWS.GOOD_FAITH_ESTIMATE;
};
